/**
 * Level thumbnail generator (Brief 008 — library previews). Renders a LevelFormat
 * to a small top-down MINIMAP PNG (colored tiles + entity markers) as a data URL,
 * generated client-side at publish time. Cheap, deterministic, no sprite/texture
 * extraction — good enough to give the community library visual life. Could be
 * upgraded to a real Pixi render later without changing the storage/contract path.
 *
 * Browser-only (uses a <canvas>); called from the editor's publish flow, never on
 * the server or in node tests.
 */
import { EntityType, type LevelFormat, TileType } from '../engine/LevelFormat';

const BG = '#0e1322';
const TILE_COLORS: Partial<Record<TileType, string>> = {
  [TileType.SOLID]: '#6b5640',
  [TileType.SEMISOLID]: '#9c7a4d',
  [TileType.GOAL]: '#ffd23f',
};
const ENTITY_COLORS: Partial<Record<EntityType, string>> = {
  [EntityType.PLAYER_START]: '#46d160',
  [EntityType.GOAL_FLAG]: '#ffd23f',
  [EntityType.GOOMBA]: '#b5651d',
  [EntityType.COIN]: '#ffe066',
  [EntityType.SPIKE]: '#e0556b',
  [EntityType.FLYER]: '#a06cff',
};

/**
 * Render `level` to a PNG data URL minimap, or null if it can't (no canvas / empty
 * dims). `maxW`/`maxH` cap the output; tiles map to integer-sized cells so the art
 * stays crisp (pixelated) at small sizes.
 */
export function renderLevelThumbnail(level: LevelFormat, maxW = 240, maxH = 96): string | null {
  const { widthTiles, heightTiles } = level.dimensions;
  if (!widthTiles || !heightTiles) return null;

  const cell = Math.max(1, Math.floor(Math.min(maxW / widthTiles, maxH / heightTiles)));
  const w = widthTiles * cell;
  const h = heightTiles * cell;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, w, h);

  for (let r = 0; r < heightTiles; r++) {
    const row = level.tiles[r];
    if (!row) continue;
    for (let c = 0; c < widthTiles; c++) {
      const col = TILE_COLORS[row[c]];
      if (col) { ctx.fillStyle = col; ctx.fillRect(c * cell, r * cell, cell, cell); }
    }
  }

  // Entity markers — at least 2px so they read even when cell == 1.
  const m = Math.max(2, cell);
  for (const e of level.entities) {
    const col = ENTITY_COLORS[e.type];
    if (!col) continue;
    ctx.fillStyle = col;
    ctx.fillRect(e.x * cell + (cell - m) / 2, e.y * cell + (cell - m) / 2, m, m);
  }

  return canvas.toDataURL('image/png');
}
